import { instance } from "api/apiBase";
import { SERVICE_TYPE } from "providers/Auth";
import { Paginated } from "types/common/paginated";
import { Renter } from "types/landlord/renters";
import { EnrollmentSource } from "types/landlord/enrollment";
import { EnrollmentData } from "views/rent-reporting-add/types";

export const QUERY_SYNC_SUBSCRIPTIONS_STATUS = "sync_subscriptions_status";
export const MUTATION_SYNC_SUBSCRIPTIONS = "sync_subscriptions";

type RentersListPayload = Paginated & {
  extra: {
    status_summary: {
      active: number;
      opted_out: number;
      inactive: number;
      invited: number;
      under_review: number;
      rejected: number;
      registered: number;
      pending_customer_action: number;
    };
  };
} & { items: Renter[] };

const api = {
  getRenters: async ({
    page = 1,
    searchTerm = ""
  } = {}): Promise<RentersListPayload> => {
    return (
      await instance.get(`/portal/v1/renters`, {
        params: {
          per_page: 10,
          page,
          search: searchTerm !== "" ? searchTerm : undefined
        }
      })
    ).data;
  },

  addRenter: async ({
    property_id,
    email,
    first_name,
    last_name,
    phone_number,
    dob,
    ssn,
    move_in_date,
    lease_end_date,
    monthly_rent_amount,
    address1,
    address2,
    city,
    state_or_province,
    postal_code,
    country,
    opt_in,
    opt_out,
    lease_month_to_month,
    no_communication,
    unit_id,
    is_default_enrollment
  }: EnrollmentData) => {
    return (
      await instance.post("/portal/v1/properties/enroll", {
        renter: {
          property_id,
          email,
          first_name,
          last_name,
          phone_number,
          dob,
          ssn,
          move_in_date,
          lease_end_date,
          monthly_rent_amount,
          from_source: "rent-reporting" as EnrollmentSource,
          address1,
          address2,
          city,
          state_or_province,
          postal_code,
          country,
          opt_in,
          opt_out,
          lease_month_to_month,
          no_communication,
          unit_id,
          is_default_enrollment
        }
      })
    ).data;
  },

  getRenter: async (renterId: string) => {
    return (await instance.get(`/portal/v1/renters/${renterId}`)).data;
  },

  getEnrollments: async ({ page = 1 } = {}) => {
    return (
      await instance.get(`/portal/v1/enrollments/`, {
        params: {
          per_page: 10,
          page
        }
      })
    ).data;
  },
  getEnrollment: async (enrollmentId) => {
    return (await instance.get(`/portal/v1/enrollments/${enrollmentId}`)).data;
  },

  updatePartnerCustomer: async (
    partnerCustomerId: string | number,
    data: {
      first_name?: string;
      last_name?: string;
      dob?: string;
    }
  ) => {
    return (
      await instance.patch(`/portal/v1/renters/${partnerCustomerId}`, data)
    ).data;
  },

  checkBulkRentersCsv: async (file) => {
    const formData = new FormData();
    formData.append("csv", file);
    return (await instance.post(`/portal/v1/properties/check_file`, formData))
      .data;
  },
  batchEnrollByCsvFile: async (file, enrollmentOption, noCommunication) => {
    const formData = new FormData();
    formData.append("csv", file);
    formData.append("opt_out", enrollmentOption.optOut);
    formData.append("opt_in", enrollmentOption.optIn);
    formData.append("no_communication", noCommunication);
    return (await instance.post(`/portal/v1/properties/batch_enroll`, formData))
      .data;
  },
  checkRentersEligibility: async (data) => {
    return (
      await instance.post(
        `/portal/v1/enrollments/check_eligibility_from_algolia`,
        {
          algolia_data: data
        }
      )
    ).data;
  },
  enrollAllRenters: async (data) => {
    return await instance.post(`/portal/v1/enrollments/enroll_from_algolia`, {
      algolia_data: data
    });
  },
  getEnrollmentsQueue: async () => {
    return await instance.get("/portal/v1/enrollments_queue");
  },
  updateRenterEnrollment: async (
    reaId: string | number,
    {
      lease_end_date,
      monthly_rent_amount,
      lease_month_to_month,
      moved_out_date
    }: {
      lease_end_date: string;
      monthly_rent_amount: number;
      lease_month_to_month: boolean;
      moved_out_date: string;
    }
  ) => {
    return (
      await instance.patch(`/portal/v1/enrollments/${reaId}`, {
        lease_end_date,
        monthly_rent_amount,
        lease_month_to_month,
        moved_out_date
      })
    ).data;
  },
  unenrollRenters: async (reaId: number[]) => {
    return (
      await instance.post(`/portal/v1/enrollments/unenroll`, {
        enrollment_ids: reaId
      })
    ).data;
  },
  submitTypeformId: async (
    kyb_response_id: string
  ): Promise<{
    id: number;
    email: string;
    name: string;
    access_key: string;
    service_type: SERVICE_TYPE;
    status: string;
    kyb_response_id: string;
  }> =>
    (
      await instance.post(`/portal/v1/partners/complete_kyb`, {
        kyb_response_id
      })
    ).data,
  signUp: async (data: {
    email: string;
    name: string;
    url: string;
    password: string;
    property_management_system: string;
    contact_first_name: string;
    contact_last_name: string;
    contact_phone_number: string;
    units: number;
  }): Promise<{
    id: number;
    email: string;
    name: string;
    access_key: string;
    service_type: SERVICE_TYPE;
    status: string;
    url: string;
    kyb_response_id: string | null;
    contact_first_name: string;
    contact_last_name: string;
    contact_phone_number: string;
    units: number;
    property_management_system: string;
  }> => (await instance.post("/portal/v1/partners", data)).data,
  updateBillingDay: async (billingDay?: number) => {
    return (
      await instance.patch(`/portal/v1/partners/`, {
        invoice_billing_day: billingDay,
        payout_billing_day: billingDay
      })
    ).data;
  }
};

export default api;
